import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { BACKEND_URL } from '../utils/config';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Users,
  ArrowLeft,
  Loader2,
  ImagePlus,
  Save,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const EditTeamPage = () => {
  const { teamPageId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [teamPage, setTeamPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    story: '',
    goal_amount: '',
    cover_image_url: '',
  });

  useEffect(() => {
    const fetchTeamPage = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/team-pages/${teamPageId}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        
        if (!response.ok) {
          throw new Error('Team page not found');
        }
        
        const data = await response.json();
        
        // Check if user is the owner
        if (data.owner_user_id !== user?.id) {
          setError('You do not have permission to edit this team page');
          return;
        }
        
        setTeamPage(data);
        setFormData({
          title: data.title || '',
          story: data.story || '',
          goal_amount: data.goal_amount || '',
          cover_image_url: data.cover_image_url || '',
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    if (teamPageId && token) {
      fetchTeamPage();
    }
  }, [teamPageId, token, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/team-pages/${teamPageId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: formData.title,
          story: formData.story,
          goal_amount: formData.goal_amount ? parseFloat(formData.goal_amount) : null,
          cover_image_url: formData.cover_image_url || null,
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update team page');
      }
      
      toast.success('Team page updated successfully!');
      navigate(`/fundraise/${teamPage.slug}`);
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
        <Footer />
      </div>
    );
  }

  if (error && !teamPage) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-16">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-foreground mb-2">Error</h1>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => navigate(-1)} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Link
          to={`/fundraise/${teamPage?.slug}`}
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Team Page
        </Link>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-emerald-500" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">Edit Team Page</h1>
          </div>
          <p className="text-muted-foreground">
            Update your fundraising page details
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Page Title *
            </label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., John's Fundraising Page"
              required
              data-testid="team-page-title-input"
            />
          </div>

          {/* Story */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Your Story
            </label>
            <Textarea
              value={formData.story}
              onChange={(e) => setFormData({ ...formData, story: e.target.value })}
              placeholder="Share why you're fundraising for this cause..."
              rows={5}
              data-testid="team-page-story-input"
            />
          </div>

          {/* Goal Amount */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Personal Goal (Optional)
            </label>
            <Input
              type="number"
              value={formData.goal_amount}
              onChange={(e) => setFormData({ ...formData, goal_amount: e.target.value })}
              placeholder="Enter your fundraising goal"
              min="0"
              step="1"
              data-testid="team-page-goal-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Set a personal fundraising goal for your page
            </p>
          </div>

          {/* Cover Image */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Cover Image URL (Optional)
            </label>
            <div className="flex gap-2">
              <Input
                value={formData.cover_image_url}
                onChange={(e) => setFormData({ ...formData, cover_image_url: e.target.value })}
                placeholder="https://example.com/image.jpg"
                data-testid="team-page-image-input"
              />
            </div>
            {formData.cover_image_url && (
              <div className="mt-3">
                <img
                  src={formData.cover_image_url}
                  alt="Preview"
                  className="w-full h-48 object-cover rounded-lg"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || !formData.title}
              className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
              data-testid="save-team-page-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </form>
      </main>
      
      <Footer />
    </div>
  );
};

export default EditTeamPage;
